/** Caller owns returned blobs and preview URLs. Retain only the latest flattened
 * blob; revoke any preview URL on replacement/unmount. Abort on context changes.
 * These helpers never create object URLs, upload, or retain the input image. */
export interface ImageTaskOptions { signal?: AbortSignal }
export interface ImageSize { width: number; height: number }
export interface ImageRectangle extends ImageSize { x: number; y: number }
const MAX_REPORT_IMAGE_BYTES = 8_388_608
export const MAX_REPORT_IMAGE_PIXELS = 25_000_000

export function checkImageAbort(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Image operation cancelled', 'AbortError')
}

/** Browser decode/encode cannot be interrupted. Reject promptly, drain the task,
 * and release late resources; never deliver a late result to the caller. */
export function abortableImageTask<T>(task: Promise<T>, signal?: AbortSignal, release?: (value: T) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    let cancelled = false
    const abort = () => { cancelled = true; reject(new DOMException('Image operation cancelled', 'AbortError')) }
    signal?.addEventListener('abort', abort, { once: true })
    if (signal?.aborted) abort()
    task.then(value => {
      signal?.removeEventListener('abort', abort)
      if (cancelled) release?.(value)
      else resolve(value)
    }, error => {
      signal?.removeEventListener('abort', abort)
      if (!cancelled) reject(error)
    })
  })
}

function checkBytes(blob: Blob): void {
  if (!blob.size || blob.size > MAX_REPORT_IMAGE_BYTES) throw new Error('Image must be non-empty and at most 8 MiB')
}
function checkPixels({ width, height }: ImageSize): void {
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width <= 0 || height <= 0 || width * height > MAX_REPORT_IMAGE_PIXELS) {
    throw new Error('Image must contain at most 25 million pixels')
  }
}

async function validateSignature(blob: Blob, signal?: AbortSignal): Promise<void> {
  checkImageAbort(signal)
  checkBytes(blob)
  const bytes = new Uint8Array(await abortableImageTask(blob.slice(0, 12).arrayBuffer(), signal))
  const signature = [...bytes]
  const matches = (wanted: number[], start = 0) => wanted.every((value, index) => signature[start + index] === value)
  const mime = matches([137, 80, 78, 71, 13, 10, 26, 10]) ? 'image/png'
    : matches([255, 216, 255]) ? 'image/jpeg'
      : matches([82, 73, 70, 70]) && matches([87, 69, 66, 80], 8) ? 'image/webp' : null
  if (!mime || (blob.type && blob.type !== mime)) throw new Error('Unsupported or mismatched image format')
}

/** Rectangle coordinates are relative to the displayed image content box, not
 * a letterboxed container. Round outward so no selected edge pixel survives. */
export function mapPreviewRectangle(rect: ImageRectangle, preview: ImageRectangle, natural: ImageSize): ImageRectangle {
  checkPixels(natural)
  if (![rect.x, rect.y, rect.width, rect.height, preview.x, preview.y, preview.width, preview.height].every(Number.isFinite) ||
    rect.width <= 0 || rect.height <= 0 || preview.width <= 0 || preview.height <= 0) throw new Error('Invalid redaction rectangle')
  const x = Math.max(0, Math.floor((rect.x - preview.x) * natural.width / preview.width))
  const y = Math.max(0, Math.floor((rect.y - preview.y) * natural.height / preview.height))
  const right = Math.min(natural.width, Math.ceil((rect.x + rect.width - preview.x) * natural.width / preview.width))
  const bottom = Math.min(natural.height, Math.ceil((rect.y + rect.height - preview.y) * natural.height / preview.height))
  if (right <= x || bottom <= y) throw new Error('Empty redaction rectangle')
  return { x, y, width: right - x, height: bottom - y }
}

async function transformImage(blob: Blob, rectangles: readonly ImageRectangle[], { signal }: ImageTaskOptions): Promise<Blob> {
  await validateSignature(blob, signal)
  checkImageAbort(signal)
  if (typeof createImageBitmap !== 'function') throw new Error('Image decoder is unavailable')
  let bitmap: ImageBitmap
  try {
    bitmap = await abortableImageTask(createImageBitmap(blob), signal, late => late.close())
  } catch (error) {
    checkImageAbort(signal)
    throw new Error('Unable to decode image', { cause: error })
  }
  const canvas = document.createElement('canvas')
  try {
    checkImageAbort(signal)
    checkPixels(bitmap)
    canvas.width = bitmap.width; canvas.height = bitmap.height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Image canvas is unavailable')
    context.drawImage(bitmap, 0, 0)
    context.fillStyle = '#000000'
    for (const rectangle of rectangles) {
      const rect = mapPreviewRectangle(rectangle, { x: 0, y: 0, width: bitmap.width, height: bitmap.height }, bitmap)
      context.fillRect(rect.x, rect.y, rect.width, rect.height)
    }
    checkImageAbort(signal)
    const output = await abortableImageTask(new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(result => result ? resolve(result) : reject(new Error('Unable to encode image')), 'image/png')
    }), signal)
    await validateSignature(output, signal)
    if (output.type !== 'image/png') throw new Error('Unexpected encoded image format')
    // Canvas dimensions are the output's decoded dimensions; encoding never resizes.
    checkPixels(canvas)
    checkImageAbort(signal)
    return output
  } finally {
    bitmap.close()
    canvas.width = 0; canvas.height = 0
  }
}

export function normalizeReportImage(blob: Blob, options: ImageTaskOptions = {}): Promise<Blob> {
  return transformImage(blob, [], options)
}

/** Rectangles use natural image pixels. The returned PNG contains overwritten
 * pixels, not reversible layers. The original is never returned or retained. */
export function flattenRedactions(blob: Blob, rectangles: readonly ImageRectangle[], options: ImageTaskOptions = {}): Promise<Blob> {
  return transformImage(blob, rectangles, options)
}
